// Core packages
import { formatDate } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

// Third party packages
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from "file-saver";
import { combineLatest, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, take, tap } from 'rxjs/operators';


// Core packages
import { MatDialog } from '@angular/material';
import { AuthService } from 'src/app/modules/auth/auth.service';
import { DocumentDownloadDialogComponent } from 'src/app/modules/properties/components/document-download-dialog/document-download-dialog.component';
import { SettingsService } from "src/app/modules/settings/settings.service";
import { ConfirmDialogComponent } from 'src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { ListSetting } from 'src/app/shared/interfaces/list-setting.interface';
import { Company } from "src/app/shared/models/company/company.model";
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from 'src/app/shared/services/helper.service';
import { ListsService } from 'src/app/shared/services/lists.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { GenericDocsDataSource } from 'src/app/shared/utils/data-sources/generic-docs-source';
import { GenericDocsAddDocDialogComponent } from '../components/add-doc-dialog/add-doc-dialog.component';
import { GenericDocsEditDocDialogComponent } from '../components/edit-doc-dialog/edit-doc-dialog.component';
import { GenericDocsQrCodeDialogComponent } from '../components/qr-code-dialog/qr-code-dialog.component';
import { GenericDocsService } from '../generic-docs.service';

@Component({
  selector: 'app-generic-docs-list',
  templateUrl: './generic-docs-list.page.html',
  styleUrls: ['./generic-docs-list.page.scss']
})
export class GenericDocsListComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserRole: any;
  currentRole: any;

  // BEGIN - Table generic docs
  displayedColumns: string[] = ['index', 'name', 'description', 'createdAt', 'actions'];
  dataSource: GenericDocsDataSource;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // @ViewChild('input', { static: true }) input: ElementRef;
  nameFilter: FormControl = new FormControl('');
  descriptionFilter: FormControl = new FormControl('');
  createdAtFilter: FormControl = new FormControl('');
  // END - Table generic docs

  company: Company = new Company();
  occupiedSpace = '';

  /**
   * Class constructor
   */
  constructor(
    public dialog: MatDialog,
    public cancelContractDialog: MatDialog,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private configService: ConfigService,
    private settingsService: SettingsService,
    private genericDocsService: GenericDocsService,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('genericDocs.list.pageTitle'));

    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));
    this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(res => this.loggedUserPermissions = res));

    // Get logged user's role data
    this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
      return (
        obj._id.toString() ===
        this.authService.loggedUserRequestedRole$.getValue().toString()
      );
    });
    this.loggedUserRole = this.currentRole.role;

    // Init data source
    this.dataSource = new GenericDocsDataSource(this.genericDocsService, this.helperService);

    // Init areas
    this.subscriptions.push(
      this.genericDocsService.getOccupiedSpace('1').subscribe(
        (res) => {
          if (res.status) {
            this.occupiedSpace = res.occupiedSpace;
          }
        },
        (err) => {
          //
        }
      )
    )

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings('genericDocs');
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => this.loadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.nameFilter.valueChanges.pipe(startWith(null as string)),
        this.descriptionFilter.valueChanges.pipe(startWith(null as string)),
        this.createdAtFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(res => {
          // Load page only if at least one value is not null
          if (!res.every(value => value === null)) {
            this.paginator.pageIndex = 0;
            this.loadPage();
          }
        }
        )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all generic docs
    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      'genericDocs',
      this.paginator.pageIndex.valueOf(),
      this.paginator.pageSize.valueOf(),
      {
        name: this.nameFilter.value,
        description: this.descriptionFilter.value,
        createdAt: this.createdAtFilter.value,
      }
    );

    this.authService.checkLastUserDocs(this.loggedUserPermissions);
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      name: this.nameFilter.value,
      description: this.descriptionFilter.value,
      createdAt: this.createdAtFilter.value,
    };
    this.dataSource.loadDocumentsItems('1',start, length, this.sort.active, this.sort.direction, search);
  }

  /**
   * Export generic docs in a file with given format
   *
   * @since 1.0.0
   */
  onExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('genericDocs.list.exportFileName')} ${date}`;
    this.subscriptions.push(
      this.genericDocsService.getExport('1').subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('genericDocs', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.nameFilter.setValue(null);
    this.descriptionFilter.setValue(null);
    this.createdAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = "desc";
    this.sort.active = "createdAt";
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe(
        (settings: ListSetting[]) => {
          if (settings.length) {
            settings.forEach((setting: ListSetting) => {
              if (setting.name === name) {
                if (setting.pageIndex) {
                  this.paginator.pageIndex = setting.pageIndex;
                }
                if (setting.pageSize) {
                  this.paginator.pageSize = setting.pageSize;
                }
                if (setting.search && setting.search.name) {
                  this.nameFilter.setValue(setting.search.name);
                }
                if (setting.search && setting.search.description) {
                  this.descriptionFilter.setValue(setting.search.description);
                }
                if (setting.search && setting.search.createdAt) {
                  this.createdAtFilter.setValue(setting.search.createdAt);
                }

              }
            });
          }
        }
      )
    );
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = '';
    const event = new KeyboardEvent('keyup');
    // this.input.nativeElement.dispatchEvent(event);
  }

  /**
   * Handler Add new doc
   */
  onAddDoc(): void {
    const dialogRef = this.dialog.open(GenericDocsAddDocDialogComponent, {
      width: "700px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            this.loadPage();
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handler Edit doc
   */
  onEdit(data: any): void {
    const dialogRef = this.dialog.open(GenericDocsEditDocDialogComponent, {
      data,
      width: "700px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            this.loadPage();
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handler Delete doc
   */
  onDelete(docId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('genericDocs.deleteDoc.title'),
        message: this.translateService.instant('genericDocs.deleteDoc.message'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.close'),
      },
      width: '500px',
      disableClose: true
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res2 => {
          if (res2) {
            this.genericDocsService.delete('1',docId)
              .subscribe(
                res => {
                  if (res.status) {
                    this.loadPage();
                  }
                },
                err => {
                  this.helperService.handleError(err);
                }
              )
          }
        }
      );
  }

  /**
   * Handler View standard QRcode doc
   */
  onViewQrcode(data: any): void {
    this.dialog.open(GenericDocsQrCodeDialogComponent, {
      data: {
        data,
        print: false,
      },
      width: "350px",
    });
  }

  /**
   * Handler Print standard QRcode doc
   */
  onPrintQrcode(data: any): void {
    this.dialog.open(GenericDocsQrCodeDialogComponent, {
      data: {
        data,
        print: true,
      },
      width: "350px",
    });
  }

  /**
   * Handler download files
   */
  onDownload(data: any): void {
    this.dialog.open(DocumentDownloadDialogComponent, {
      data: {
        documents: data.files,
      },
      width: "600px",
    });
  }
}
